import React, { useState, useEffect } from 'react';
import { AlertCircle, X, Bot, Check, Loader2, Info, Store as StoreIcon } from 'lucide-react';
import { authService } from '../services/authService';
import { getApiBaseUrl } from '../services/api';
import { AdminLoading } from './admin/AdminLoading';
import { AdminPageHeader } from './admin/AdminPageHeader';
import { AdminAddButton } from './admin/AdminAddButton';
import { useConfirmDialog } from './admin/ConfirmDialog';
import { AdminActionButton, AdminRowActions } from './admin/AdminActionButton';

const IntentManagement = () => {
  const [intents, setIntents] = useState([]);
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { confirm, confirmDialog } = useConfirmDialog();
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingIntent, setEditingIntent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [formData, setFormData] = useState({
    storeIdFk: '',
    intentName: '',
    category: '',
    description: '',
    enabled: true
  });

  useEffect(() => {
    fetchStores();
  }, []);

  useEffect(() => {
    if (selectedStore) {
      fetchIntents();
    } else {
      setLoading(false);
      setIntents([]);
      setCategories([]);
    }
  }, [selectedStore, selectedCategory]);

  const fetchStores = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/admin/stores`, {
        headers: authService.getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setStores(data);
      }
    } catch (err) {
      console.error('Error fetching stores:', err);
    }
  };

  const fetchIntents = async () => {
    setError('');
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedStore) params.append('storeId', selectedStore);
      if (selectedCategory) params.append('category', selectedCategory);
      const url = `${getApiBaseUrl()}/admin/intents?${params.toString()}`;
      const response = await fetch(url, {
        headers: authService.getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setIntents(data);
        const uniqueCategories = [...new Set(data.map(intent => intent.category).filter(Boolean))];
        setCategories(uniqueCategories);
      } else {
        setError('Failed to fetch intents');
      }
    } catch (err) {
      setError('Error fetching intents');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const url = editingIntent
        ? `${getApiBaseUrl()}/admin/intents/${editingIntent.id}`
        : `${getApiBaseUrl()}/admin/intents`;

      const method = editingIntent ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeaders()
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        await fetchIntents();
        handleCloseDialog();
        setSuccess(editingIntent ? 'Intent updated successfully' : 'Intent created successfully');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const errorData = await response.text();
        setError(errorData || 'Failed to save intent');
      }
    } catch (err) {
      setError('Error saving intent');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (intent) => {
    setError('');
    setSuccess('');
    setEditingIntent(intent);
    setFormData({
      storeIdFk: intent.storeIdFk ? String(intent.storeIdFk) : '',
      intentName: intent.intentName,
      category: intent.category || '',
      description: intent.description || '',
      enabled: intent.enabled
    });
    setOpenDialog(true);
  };

  const handleDelete = async (id) => {
    const intent = intents.find((i) => i.id === id);
    const label = intent?.intentName ? `"${intent.intentName}"` : 'this intent';
    const confirmed = await confirm({
      title: `Delete intent ${label}?`,
      message: `The intent ${label} will be permanently deleted.`,
      confirmLabel: 'Delete',
    });
    if (!confirmed) return;
    setDeletingId(id);
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`${getApiBaseUrl()}/admin/intents/${id}`, {
        method: 'DELETE',
        headers: authService.getAuthHeaders()
      });
      if (response.ok) {
        await fetchIntents();
        setSuccess('Intent deleted successfully');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Failed to delete intent');
      }
    } catch (err) {
      setError('Error deleting intent');
    } finally {
      setDeletingId(null);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingIntent(null);
    setFormData({
      storeIdFk: selectedStore ? String(selectedStore) : '',
      intentName: '',
      category: '',
      description: '',
      enabled: true
    });
    setError('');
    setSuccess('');
  };

  const handleToggleEnabled = async (intent) => {
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`${getApiBaseUrl()}/admin/intents/${intent.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeaders()
        },
        body: JSON.stringify({
          ...intent,
          enabled: !intent.enabled
        }),
      });
      if (response.ok) {
        await fetchIntents();
      } else {
        setError('Failed to update intent');
      }
    } catch (err) {
      setError('Error updating intent');
    }
  };

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Intents"
        description="Manage store-specific intents used by the N2 AI assistant to understand and respond to customer messages"
      />

      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-blue-800 leading-relaxed">
          Intents tell the AI how to interpret customer messages. Select a store to view its active intents, filter by category, or add new ones to guide the conversation.
        </div>
      </div>

      {success && (
        <div className="flex items-center p-3 bg-green-50 border border-green-200 rounded-lg">
          <Check className="w-4 h-4 text-green-600 mr-2" />
          <span className="text-sm text-green-700">{success}</span>
        </div>
      )}

      {error && (
        <div className="flex items-center p-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
          <span className="text-sm text-red-700">{error}</span>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <h3 className="text-sm font-medium text-gray-900">
              Intents ({intents.length})
            </h3>
            <select
              value={selectedStore || ''}
              onChange={(e) => setSelectedStore(e.target.value ? Number(e.target.value) : null)}
              className="text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select a store</option>
              {stores.map((store) => (
                <option key={store.id} value={store.id}>
                  {store.name || store.storeId}
                </option>
              ))}
            </select>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Categories</option>
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>
          {selectedStore && <AdminAddButton onClick={() => setOpenDialog(true)} />}
        </div>

        {!selectedStore ? (
          <div className="p-8 text-center text-gray-500">
            <StoreIcon className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p className="text-sm">Please select a store to view intents</p>
          </div>
        ) : loading ? (
          <AdminLoading />
        ) : intents.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Bot className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p className="text-sm">No intents configured yet for this store</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {intents.map((intent) => (
              <div key={intent.id} className="p-4 flex items-start justify-between hover:bg-gray-50 transition">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="text-sm font-medium text-gray-900">
                      {intent.intentName}
                    </h4>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      intent.enabled
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {intent.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                    {intent.category && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                        {intent.category}
                      </span>
                    )}
                  </div>
                  {intent.description && (
                    <p className="text-sm text-gray-500 mt-1">{intent.description}</p>
                  )}
                </div>
                <AdminRowActions className="ml-4">
                  <AdminActionButton variant="edit" onClick={() => handleEdit(intent)} />
                  <AdminActionButton variant={intent.enabled ? 'deactivate' : 'activate'} label={intent.enabled ? 'Disable' : 'Enable'} onClick={() => handleToggleEnabled(intent)} />
                  <AdminActionButton variant="delete" loading={deletingId === intent.id} loadingLabel="Deleting…" onClick={() => handleDelete(intent.id)} />
                </AdminRowActions>
              </div>
            ))}
          </div>
        )}
      </div>

      {openDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900">
                {editingIntent ? 'Edit Intent' : 'Add New Intent'}
              </h3>
              <button
                onClick={handleCloseDialog}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              {error && (
                <div className="flex items-center p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
                  <span className="text-xs text-red-700">{error}</span>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Store *
                </label>
                <select
                  required
                  value={formData.storeIdFk}
                  onChange={(e) => setFormData({ ...formData, storeIdFk: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Select a store</option>
                  {stores.map((store) => (
                    <option key={store.id} value={store.id}>
                      {store.name || store.storeId}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Intent Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.intentName}
                  onChange={(e) => setFormData({ ...formData, intentName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="e.g., SHUTTLE_SERVICE_INQUIRY"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="e.g., Service, Sales, General"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  rows="3"
                  placeholder="Describe when this intent should be used"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="enabled"
                  checked={formData.enabled}
                  onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <label htmlFor="enabled" className="ml-2 text-sm text-gray-700">
                  Enabled
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseDialog}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-70 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  {saving ? 'Saving...' : (editingIntent ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {confirmDialog}
    </div>
  );
};

export default IntentManagement;
